/* The review aids above the recommendation tiers: filters, where the list came from, and what the
 * profile showed that no rule expresses.
 *
 * **Where the list came from is stated, not implied.** Recommendations are now generated after every
 * profile, with nobody watching, so a reader opening this screen may be looking at a run they did not
 * cause. The run note names the trigger and the time, and says whether the profile informed it —
 * because "these came from the schema alone" and "these came from the schema and 48,000 profiled
 * rows" are different strengths of evidence for the same-looking table.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { SelectField, TextField } from '@/shared/ui/Field';
import { SegmentedFilter } from '@/shared/ui/SegmentedFilter';

import type { GenerationRun, ProfileObservation, ProfileStatus } from '../api/rulegen';
import type { StatusFilter, SuggestionFilter } from '../model/presentation';

const CATEGORIES = [
  '',
  'completeness',
  'uniqueness',
  'validity',
  'timeliness',
  'consistency',
  'integrity',
] as const;
type CategoryChoice = (typeof CATEGORIES)[number];
const ORIGINS = ['', 'system', 'ai', 'hybrid'] as const;
type OriginChoice = (typeof ORIGINS)[number];

export function FilterBar({
  filter,
  counts,
  originCounts,
  onChange,
}: {
  readonly filter: SuggestionFilter;
  readonly counts: Readonly<Record<StatusFilter, number>>;
  /** How many each Source choice would show, keyed `''`, `system`, `ai`, `hybrid`. */
  readonly originCounts: Readonly<Record<string, number>>;
  readonly onChange: (next: SuggestionFilter) => void;
}): ReactElement {
  const { t } = useTranslation();
  const statuses: readonly StatusFilter[] = ['open', 'applied', 'dismissed', 'all'];
  return (
    <div className="lq-sg__filters">
      <SegmentedFilter
        label={t('suggestions.filter.status')}
        onChange={(status) => {
          onChange({ ...filter, status });
        }}
        options={statuses.map((id) => ({
          id,
          label: t(`suggestions.filter.status.${id}`),
          count: counts[id],
        }))}
        value={filter.status}
        variant="ledger"
      />
      <div className="lq-sg__filter-fields">
        <SelectField<CategoryChoice>
          label={t('suggestions.filter.category')}
          onChange={(category) => {
            onChange({ ...filter, category });
          }}
          options={CATEGORIES.map((value) => ({
            value,
            label: t(`suggestions.filter.category.${value === '' ? 'any' : value}`),
          }))}
          value={filter.category as CategoryChoice}
        />
        <SelectField<OriginChoice>
          label={t('suggestions.filter.origin')}
          onChange={(origin) => {
            onChange({ ...filter, origin });
          }}
          options={ORIGINS.map((value) => ({
            value,
            label: t('suggestions.filter.withCount', {
              label: t(`suggestions.filter.origin.${value === '' ? 'any' : value}`),
              count: originCounts[value] ?? 0,
            }),
          }))}
          value={filter.origin as OriginChoice}
        />
        <TextField
          label={t('suggestions.filter.column')}
          onChange={(column) => {
            onChange({ ...filter, column });
          }}
          type="search"
          value={filter.column}
        />
      </div>
    </div>
  );
}

const PROFILE_TONE = {
  used: 'ok',
  never_profiled: 'unknown',
  stale: 'warn',
  empty: 'unknown',
} satisfies Record<ProfileStatus, string>;

/** Which run produced the list on screen, and how much evidence it had. */
export function RunNote({
  run,
  profileStatus,
}: {
  readonly run: GenerationRun | null;
  readonly profileStatus: ProfileStatus | undefined;
}): ReactElement | null {
  const { t } = useTranslation();
  const status = profileStatus ?? run?.profile_status;
  if (run === null && status === undefined) return null;
  return (
    <div className="lq-sg__run-note">
      {run !== null && (
        <span>
          {t(
            run.trigger === 'manual'
              ? 'suggestions.run.manual'
              : `suggestions.run.${run.trigger}`,
            {
              when: new Date(run.generated_at).toLocaleString(),
              actor: run.actor,
            },
          )}
        </span>
      )}
      {status !== undefined && (
        <span
          className={`lq-sg__chip lq-sg__chip--sm lq-sg__chip--${PROFILE_TONE[status]}`}
        >
          <span className="lq-sg__dot lq-sg__dot--filled" />
          {t(`suggestions.profile.${status}`)}
        </span>
      )}
    </div>
  );
}

/** Patterns the profile showed that no proposable rule expresses. Never a value from the data. */
export function Observations({
  observations,
}: {
  readonly observations: readonly ProfileObservation[];
}): ReactElement | null {
  const { t } = useTranslation();
  if (observations.length === 0) return null;
  return (
    <section aria-labelledby="suggestion-observations" className="lq-sg__panel">
      <div className="lq-sg__strip lq-sg__strip--plain">
        <div className="lq-sg__bounds-main">
          <h2 className="lq-sg__eyebrow lq-sg__bounds-title" id="suggestion-observations">
            {t('suggestions.observations.title')}
          </h2>
          <div className="lq-sg__note">{t('suggestions.observations.hint')}</div>
        </div>
      </div>
      <ul className="lq-sg__observations">
        {observations.map((observation) => (
          <li
            className="lq-sg__observation"
            key={`${observation.column}:${observation.kind}`}
          >
            <span className="lq-sg__mono">{observation.column}</span>
            <span className="lq-sg__observation-kind">
              {t(`suggestions.observations.${observation.kind}`)}
            </span>
            <span className="lq-sg__observation-detail">{observation.detail}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
